import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, FileText, ExternalLink, Image, RefreshCw, Send, Archive } from 'lucide-react';
import { EduArticle, EduDomain, EduSubject, EduKnowledgePoint } from '../../../types/learning';
import { API_CONFIG } from '../../../config/api';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const ArticleManagement: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<EduArticle[]>([]);
  const [domains, setDomains] = useState<EduDomain[]>([]);
  const [subjects, setSubjects] = useState<EduSubject[]>([]);
  const [knowledgePoints, setKnowledgePoints] = useState<EduKnowledgePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // 当领域选择变化时重新获取学科数据
  useEffect(() => {
    if (domainFilter && domainFilter !== 'all') {
      setSubjectsLoading(true);
      fetchSubjectsByDomain(domainFilter).finally(() => setSubjectsLoading(false));
    } else {
      // 如果选择全部领域，获取所有学科
      setSubjectsLoading(true);
      fetchSubjects().finally(() => setSubjectsLoading(false));
    }
  }, [domainFilter]);

  // 当筛选条件变化时重新获取文章数据
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchArticles();
    }, 300); // 防抖处理，避免频繁请求
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, domainFilter, subjectFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchArticles(),
        fetchDomains(),
        fetchSubjects(),
        fetchKnowledgePoints()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchData();
      toast.success('数据刷新成功');
    } catch (error) {
      console.error('刷新失败:', error);
      toast.error('刷新失败');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchArticles = async () => {
    try {
      // 构建查询参数
      const params = new URLSearchParams({
        action: 'list',
        limit: '100'
      });
      
      // 添加筛选参数
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (domainFilter !== 'all') {
        params.append('domain_id', domainFilter);
      }
      
      if (subjectFilter !== 'all') {
        params.append('subject_id', subjectFilter);
      }
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-articles-admin-manage?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch articles');

      const result = await response.json();
      if (result.success) {
        setArticles(result.data.data || []);
      }
    } catch (error) {
      console.error('获取文章列表失败:', error);
      toast.error('获取文章列表失败');
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-domains-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch domains');

      const result = await response.json();
      if (result.success) {
        setDomains(result.data.data || []);
      }
    } catch (error) {
      console.error('获取领域列表失败:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch subjects');

      const result = await response.json();
      if (result.success) {
        setSubjects(result.data.data || []);
      }
    } catch (error) {
      console.error('获取学科列表失败:', error);
    }
  };

  const fetchSubjectsByDomain = async (domainId: string) => {
    try {
      // 构建查询参数
      const params = new URLSearchParams({
        action: 'list',
        limit: '100'
      });
      
      // 如果指定了领域ID，则只获取该领域下的学科
      if (domainId && domainId !== 'all') {
        params.append('domain_id', domainId);
      }
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch subjects');

      const result = await response.json();
      if (result.success) {
        setSubjects(result.data.data || []);
      }
    } catch (error) {
      console.error('获取学科列表失败:', error);
    }
  };

  const fetchKnowledgePoints = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch knowledge points');

      const result = await response.json();
      if (result.success) {
        setKnowledgePoints(result.data.data || []);
      }
    } catch (error) {
      console.error('获取知识点列表失败:', error);
    }
  };

  const handleCreate = () => {
    navigate('/admin/learning/article/new');
  };

  const handleEdit = (article: EduArticle) => {
    navigate(`/admin/learning/article/edit/${article.id}`);
  };

  const handleDelete = async (article: EduArticle) => {
    if (!confirm(`确定要删除文章「${article.title}」吗？此操作不可撤销。`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-articles-admin-manage?action=delete&id=${article.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete article');

      const result = await response.json();
      if (result.success) {
        toast.success('文章删除成功');
        await fetchArticles();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('删除文章失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleRegenerate = async (article: EduArticle) => {
    if (!confirm(`确定要重新生成文章「${article.title}」吗？这将覆盖现有内容。`)) return;

    try {
      // 先获取知识点信息
      const knowledgePoint = knowledgePoints.find(p => p.id === article.point_id);
      if (!knowledgePoint) {
        toast.error('找不到对应的知识点信息');
        return;
      }

      const subject = subjects.find(s => s.id === article.subject_id);
      const domain = domains.find(d => d.id === article.domain_id);

      toast.loading('正在重新生成文章...', { id: 'regenerate' });

      // 调用AI内容生成接口，更新现有文章
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/aimodule-content-admin-generate-edu?type=knowledge_article`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          knowledge_point_id: article.point_id,
          knowledge_point_name: knowledgePoint.name,
          subject_name: subject?.name || '未知学科',
          domain_name: domain?.name || '未知领域',
          description: knowledgePoint.description || '',
          update_existing: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'API调用失败');
      }

      const result = await response.json();
      if (result.success) {
        toast.success('文章重新生成成功', { id: 'regenerate' });
        await fetchArticles(); // 刷新文章列表
      } else {
        throw new Error(result.error || '重新生成失败');
      }
    } catch (error) {
      console.error('重新生成文章失败:', error);
      toast.error('重新生成失败：' + (error as Error).message, { id: 'regenerate' });
    }
  };

  const handleToggleStatus = async (article: EduArticle) => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-articles-admin-manage?action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: article.id,
          status: article.status === 1 ? 0 : 1
        })
      });

      if (!response.ok) throw new Error('Failed to toggle status');

      const result = await response.json();
      if (result.success) {
        toast.success(`文章已${article.status === 1 ? '下线' : '发布'}`);
        await fetchArticles();
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      toast.error('操作失败');
    }
  };

  const getDomainName = (domainId: string) => {
    const domain = domains.find(d => d.id === domainId);
    return domain?.name || '未知领域';
  };

  const getSubjectName = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.name || '未知学科';
  };

  const getKnowledgePointName = (pointId: string) => {
    const point = knowledgePoints.find(p => p.id === pointId);
    return point?.name || '未知知识点';
  };

  // 使用后端已筛选的数据，无需前端再次筛选
  const filteredArticles = articles;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-blue-600" />
              文章管理
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center px-3 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="刷新数据"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                刷新
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                创建文章
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索文章标题、摘要或作者..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={domainFilter}
              onChange={(e) => {
                setDomainFilter(e.target.value);
                setSubjectFilter('all'); // 当领域改变时，重置学科选择
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部领域</option>
              {domains.map(domain => (
                <option key={domain.id} value={domain.id}>{domain.name}</option>
              ))}
            </select>
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              disabled={subjectsLoading}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">{subjectsLoading ? '加载中...' : '全部学科'}</option>
              {!subjectsLoading && subjects.filter(s => s.status === 1).map(subject => (
                <option key={subject.id} value={subject.id}>{subject.name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="published">已发布</option>
              <option value="draft">草稿</option>
            </select>
          </div>

          {/* 文章列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    文章信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    层级结构
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    作者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    统计
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start space-x-3">
                        {article.cover_image && (
                          <img
                            src={article.cover_image}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {article.title}
                          </div>
                          {article.summary && (
                            <div className="text-sm text-gray-500 line-clamp-2">
                              {article.summary}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        <div>{getDomainName(article.domain_id)}</div>
                        <div>{getSubjectName(article.subject_id)}</div>
                        <div>{getKnowledgePointName(article.point_id)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{article.author || '未知'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        <div>阅读: {article.read_count}</div>
                        <div>点赞: {article.like_count}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(article)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          article.status === 1
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        }`}
                      >
                        {article.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />已发布</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />草稿</>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(article.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => window.open(`/learning/article/${article.id}`, '_blank')}
                          className="text-gray-600 hover:text-gray-900"
                          title="预览"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(article)}
                          className={`${
                            article.status === 1
                              ? 'text-orange-600 hover:text-orange-900'
                              : 'text-green-600 hover:text-green-900'
                          }`}
                          title={article.status === 1 ? '下线文章' : '发布文章'}
                        >
                          {article.status === 1 ? (
                            <Archive className="w-4 h-4" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRegenerate(article)}
                          className="text-blue-600 hover:text-blue-900"
                          title="重新生成"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(article)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="编辑"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(article)}
                          className="text-red-600 hover:text-red-900"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无文章数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 创建/编辑模态框已移除，现在跳转到专门的编辑页面 */}
    </div>
  );
};

export default ArticleManagement;